/** Google OAuth callback — handles the `#session_id=<id>` fragment that
 *  Emergent Auth appends to /dashboard after Google consent.
 *
 *  REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
 *  URLS, THIS BREAKS THE AUTH.
 *
 *  Two responsibilities:
 *    1. If URL hash contains `#session_id=<id>`: exchange it for our
 *       WaveScorePro JWT via POST /api/auth/google/exchange, store the
 *       token in the SAME localStorage keys used by email/password login
 *       (see `/app/frontend/src/lib/api.js` — `wsp_token`, `wsp_refresh`),
 *       then navigate to /app/dashboard.
 *    2. If URL hash is empty (someone typed /dashboard or clicked a
 *       nav-bar link): treat this as an alias for /app/dashboard and
 *       redirect immediately. Do NOT force the user to /login — that
 *       kicks signed-in users out of the app.
 */
import { useEffect, useRef } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { T } from "@/lib/theme";

const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";

// localStorage keys — MUST match `/app/frontend/src/lib/api.js`.
const TOKEN_KEY   = "wsp_token";
const REFRESH_KEY = "wsp_refresh";
const USER_KEY    = "wsp_user";

function extractSessionId(hash) {
  if (!hash) return null;
  // Support both `#session_id=<x>` and `#/?session_id=<x>` shapes.
  const clean = hash.replace(/^#\/?/, "");
  const params = new URLSearchParams(clean);
  return params.get("session_id");
}

export default function AuthCallback() {
  const nav = useNavigate();
  const processed = useRef(false);
  const sessionId = extractSessionId(window.location.hash);

  useEffect(() => {
    if (processed.current) return;
    if (!sessionId) return;         // Empty-hash visits fall through to <Navigate> below.
    processed.current = true;
    axios.post(`${API_BASE}/auth/google/exchange`, { session_id: sessionId })
      .then(({ data }) => {
        localStorage.setItem(TOKEN_KEY,   data.access_token);
        localStorage.setItem(REFRESH_KEY, data.refresh_token);
        localStorage.setItem(USER_KEY,    JSON.stringify(data.user));
        // Clean the fragment so a reload doesn't re-run the exchange.
        window.location.hash = "";
        nav("/app/dashboard", { replace: true });
      })
      .catch((err) => {
        const detail = err.response?.data?.detail || "Google sign-in failed";
        nav(`/login?google_error=${encodeURIComponent(detail)}`, { replace: true });
      });
  }, [sessionId, nav]);

  if (!sessionId) {
    // Someone hit /dashboard directly (nav-bar link, bookmark, typo).
    // Treat as an alias for /app/dashboard — the Guard on that route
    // handles the actual signed-in check.
    return <Navigate to="/app/dashboard" replace />;
  }

  return (
    <div
      data-testid="auth-callback"
      style={{
        minHeight: "100vh",
        display: "flex", alignItems: "center", justifyContent: "center",
        background: T.dark.bg, color: T.dark.text, fontFamily: "'Inter',sans-serif",
      }}
    >
      <div style={{ textAlign: "center" }}>
        <div style={{
          width: 40, height: 40, margin: "0 auto 18px",
          border: `3px solid ${T.dark.border}`, borderTopColor: T.greenAccent,
          borderRadius: "50%",
          animation: "wsp-spin 1s linear infinite",
        }} />
        <div style={{ fontSize: 15, fontWeight: 600 }}>Signing you in with Google…</div>
        <div style={{ fontSize: 12, color: T.dark.muted, marginTop: 6 }}>
          Exchanging your session for a WaveScorePro token.
        </div>
      </div>
      <style>{`@keyframes wsp-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
